import React from 'react';
import { Card, CardVariants, mergeClassNames, Text } from '@clockout-dev/design-elements';
import styles from './styles/metricCard.module.scss';

/**
 * Reusable metric tile shared by dashboard / cashflow KPI cards. Wraps
 * the design-elements `Card` so the chrome (radius, padding, shadow,
 * highlight gradient) stays in the design system. This component owns
 * only the inner header arrangement: an icon and a "label + description"
 * block laid out horizontally; everything else is composed by the
 * consumer via `children`.
 *
 * Props:
 *   - `image`     icon node rendered in the leading chip
 *   - `title`     `{ label, description }` rendered immediately to the
 *                 right of the icon — `label` is the small uppercase
 *                 muted text, `description` is the bold dark text below
 *   - `action`    optional node anchored to the trailing edge of the
 *                 title row (status pills, action buttons, etc.)
 *   - `children`  card body rendered below the title row
 *   - `variant`   forwarded to the underlying Card —
 *                 `CardVariants.HIGHLIGHT` switches to the navy/blue
 *                 gradient and inverts the inner text colors
 *   - `className` optional extra class merged onto the Card root
 */
function MetricCard({ image, title, action, children, variant, className, classNames }) {
    const isHighlighted = variant === CardVariants.HIGHLIGHT;
    const cardClassName = mergeClassNames([
        styles.card,
        ...(isHighlighted ? [styles.highlighted] : []),
        className,
    ]);
    const imageClassName = mergeClassNames([
        styles.iconBox,
        ...(classNames?.icon ? [classNames.icon] : []),
    ]);

    return (
        <Card variant={variant} className={cardClassName}>
            <div className={styles.titleRow}>
                <div className={styles.titleRowContent}>
                    {image ? <span className={imageClassName}>{image}</span> : null}
                    {title ? (
                        <div className={styles.titleBlock}>
                            {title.label ? <Text size="xs" className={styles.label}>{title.label}</Text> : null}
                            {title.description ? <Text size="m" className={styles.description}>{title.description}</Text> : null}
                        </div>
                    ) : null}
                </div>
                {action ? <div className={styles.action}>{action}</div> : null}
            </div>
            {children ? children : null}
        </Card>
    );
}

export default MetricCard;
